<template>
    <div>
        <div v-if="isDisabled" class="max-w-640 mb-24">
            <banner
                :title="$t('vdab.disabled')"
                warning
                inline
            />
        </div>

        <div v-else>
            <div>
                <section class="mb-40">
                    <h2 class="text-body font-semibold mb-12">
                        {{ $t('vdab.job_details') }}
                    </h2>
                    <div class="grid grid-cols-2 gap-12 mb-24">
                        <select-dropdown
                            :autoTranslate="false"
                            class="col-span-2 w-full mb-20"
                            :disabled="!creating"
                            :options="locations"
                            v-if="useLocationSelector"
                            v-model="form.company_location_id"
                        >
                            <span class="font-semibold">
                                {{ $t('vdab.job_publisher') }}
                            </span>
                            <more-info :message="$t('vdab.label_warning').toString()" type="warning" class="mb-0" />
                        </select-dropdown>
                        <div class="self-end">
                            <simple-input
                                :input-label="$t('general.reference') + ' *'"
                                :disabled="!creating"
                                v-model="form.reference"
                            >
                                <template #tip>
                                    {{ $t('vdab.label_warning') }}
                                </template>
                            </simple-input>
                            <p v-if="form.reference.length > 30" class="text-center italic">
                                {{ $t('vdab.reference_error') }}
                            </p>
                        </div>
                        <simple-input
                            class="self-end"
                            :input-label="$t('jobs.fillable_positions')"
                            :min="1"
                            type="number"
                            v-model="form.number_to_fill"
                        />
                    </div>

                    <div class="grid grid-cols-2 gap-12 mb-24">
                        <simple-input
                            :input-label="$t('vdab.job.title')"
                            v-model="form.title"
                        >
                            <template #tip>
                                {{ $t('vdab.tips.job_title') }}
                            </template>
                        </simple-input>
                        <div class="flex flex-col justify-end">
                            <select-dropdown
                                class="w-full"
                                :input-label="$t('campaigns.editor.details.language')"
                                :options="getCurrentlyActiveJobsiteLanguages"
                                v-model="form.locale"
                                @change="replaceTranslatableFields($event)"
                            >
                                {{ $t('campaigns.editor.details.language') }}
                            </select-dropdown>
                        </div>
                    </div>

                    <div class="grid grid-cols-2 gap-12 mb-24">
                        <toggle
                            v-model="permanentJob"
                            class="col-span-2"
                            :disabled="[0,1,3,8,13,14,15,18,21].includes(this.job.contract_type)"
                        >
                            {{ $t('vdab.permanent_job') }}
                        </toggle>

                        <date-picker
                            class="mr-16"
                            :input-label="$t('vdab.start_date') + ' (' + $t('validation.optional') + ')'"
                            v-model="form.contract_start_date"
                            v-show="!permanentJob"
                        />
                        <date-picker
                            :input-label="$t('vdab.end_date')"
                            v-model="form.contract_end_date"
                            v-show="!permanentJob"
                        />
                    </div>

                    <wysiwyg
                        ref="description"
                        v-model="form.description"
                        :maxSize="4000"
                        class="mt-16"
                        full-featured
                    >
                        {{ $t('vdab.job.description') }}
                        <template #description>
                            {{ $t('vdab.tips.job_description') }}
                        </template>
                    </wysiwyg>

                    <wysiwyg
                        ref="profile"
                        v-model="form.profile"
                        :maxSize="4000"
                        class="mt-16"
                        full-featured
                    >
                        {{ $t('vdab.job.profile') }}
                        <template #description>
                            {{ $t('vdab.tips.general_musts') }}
                        </template>
                    </wysiwyg>

                    <wysiwyg
                        ref="offer"
                        v-model="form.offer"
                        :maxSize="4000"
                        class="mt-16"
                        full-featured
                    >
                        {{ $t('vdab.job.offer') }}
                        <template #description>
                            {{ $t('vdab.tips.offer_benefits') }}
                        </template>
                    </wysiwyg>
                </section>

                <section class="mb-40">
                    <h2 class="text-body font-semibold first-letter:uppercase mb-24">
                        {{ $t('vdab.desired_profile') }}
                    </h2>

                    <div class="mb-32">
                        <simple-input
                            :input-label="$t('vdab.experience') + ' *'"
                            :min="0"
                            required
                            type="number"
                            v-model="form.experience"
                        >
                            <template #tip>
                                {{ $t('vdab.tips.required_skills') }}
                            </template>
                            <template #after>
                                {{ $t('vdab.experience_years') }}
                            </template>
                        </simple-input>
                    </div>

                    <studies-selector
                        v-model="form.studies"
                        class="mb-32"
                        :show="form.study_required"
                    >
                        <template #title>
                            <p class="text-body-sm font-semibold text-black">
                                {{ $t('vdab.studies') }}
                            </p>
                        </template>
                        <template #header-toggle>
                            <div class="text-semantic-info flex items-start gap-4 mb-8">
                                <svg-icon type="info" class="w-18 h-18 mt-2" />
                                {{ $t('vdab.tips.studies') }}
                            </div>
                            <toggle
                                v-model="form.study_required"
                                class="mb-8"
                            >
                                {{ $t('vdab.studies_are_required') }}
                            </toggle>
                            <toggle
                                v-if="form.study_required"
                                v-model="form.experience_is_enough"
                                class="mb-8"
                            >
                                {{ $t('vdab.but_experience_enough') }}
                            </toggle>
                        </template>
                    </studies-selector>

                    <driver-licences-selector
                        :currentLicences="form.driver_licences"
                        @select="setDriverLicences"
                    >
                        <p class="text-body-sm font-semibold text-black mb-4">
                            {{ $t('vdab.driver_licences') }}
                        </p>
                    </driver-licences-selector>

                    <languages-selector
                        :currentLanguages="form.languages"
                        class="w-full"
                        @select="setLanguages"
                    >
                        <p class="text-body-sm font-semibold text-black mb-4">
                            {{ $t('general.languages') }}
                            <span>*</span>
                        </p>
                        <div class="text-semantic-info flex items-start gap-4 mb-8">
                            <svg-icon type="info" class="w-18 h-18 mt-2" />
                            {{ $t('vdab.tips.languages') }}
                        </div>
                    </languages-selector>
                </section>

                <section class="mb-40">
                    <h2 class="text-body font-semibold capitalize mb-32 w-full">
                        {{ $t('general.template') }}
                    </h2>
                    <h3 class="text-body-sm font-semibold capitalize">
                        {{ $t('vdab.job_template') }}
                        <span>*</span>
                    </h3>
                    <div class="text-semantic-info flex items-start gap-4 mb-8">
                        <svg-icon type="info" class="w-18 h-18 mt-2" />
                        {{ $t('vdab.tips.competency_template') }}
                    </div>
                    <div class="flex flex-grow items-baseline justify-between">
                        <job-category-selector
                            :currentCategory="form.category"
                            @select="changeCategory"
                        />
                    </div>

                    <loading v-if="fetchingCategory" class="mt-60" size="medium"></loading>
                    <div v-else-if="form.category.name" class="mt-40">
                        <h4
                            v-if="form.category"
                            class="font-semibold capitalize mb-8">
                            {{ form.category.name }}
                        </h4>
                        <div v-if="form.category" class="mb-40">
                            {{ form.category.description }}
                        </div>
                        <div class="w-full space-y-12">
                            <p v-if="availableServCompetences.length" class="text-body-sm font-semibold">
                                {{ ucfirst($t('vdab.competences')) }}
                                <span>*</span>
                            </p>
                            <div class="flex items-center gap-12">
                                <search
                                    class="grow"
                                    v-model="competenceFilter"
                                />
                                <simple-button
                                    :icon="showCategoryCompetences ? 'keyboard-arrow-up' : 'keyboard-arrow-down'"
                                    look="icon"
                                    small
                                    @click="toggleCategoryCompetences"
                                />
                            </div>

                            <banner
                                :description="$t('vdab.tips.competency_template_suggestion')"
                                inline
                                info
                            />
                        </div>

                        <transition name="slide-fade">
                            <div
                                v-show="showCategoryCompetences"
                                class="w-full trans-ease mt-16">
                                <div
                                    v-if="filteredServCompetences.length"
                                    class="max-h-480 overflow-y-auto rounded border border-grey-light">
                                    <div
                                        v-for="serv in filteredServCompetences"
                                        :key="'serv-' + serv.id"
                                        class="w-full mb-8">
                                        <div class="mr-8 relative">
                                            <checkbox
                                                v-model="form.serv_ids"
                                                :value="serv.id"
                                                class="pl-8 mr-16 flex-none">
                                                <div class="pt-8 mb-8">
                                                    {{ serv.name }}
                                                </div>
                                            </checkbox>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </transition>
                    </div>
                </section>

                <section class="mb-40">
                    <h2 class="text-body font-semibold capitalize mb-4">
                        {{ $t('vdab.publication_details') }}
                    </h2>

                    <div class="mb-16">
                        <div class="text-semantic-info flex items-start gap-4 mb-8">
                            <svg-icon type="info" class="w-18 h-18 mt-2" />
                            {{ $t('vdab.tips.publish_time') }}
                        </div>
                    </div>

                    <div class="grid grid-cols-2 mb-12 gap-16">
                        <date-picker
                            :disabled="!creating"
                            :input-label="$t('general.from') + ' *'"
                            show-warning-icon
                            v-model="form.start_date"
                            :warning-message="$t('vdab.label_warning')"
                        />
                        <date-picker
                            :disabled="!creating"
                            :input-label="$t('general.to') + ' *'"
                            show-warning-icon
                            v-model="form.end_date"
                            :warning-message="$t('vdab.label_warning')"
                        />
                    </div>

                    <div
                        v-if="form.category && form.category.knelpuntberoep"
                        class="pt-32 mb-32 flex-grow flex-wrap"
                    >
                        <h2 class="text-body font-semibold w-full">
                            {{ $t('vdab.full_service_title') }}
                        </h2>

                        <p class="my-16">{{ $t('vdab.full_service_description') }}</p>

                        <checkbox v-model="form.full_service" class="mb-8">
                            {{ $t('vdab.full_service_checkbox') }}
                        </checkbox>

                        <template v-if="form.full_service">
                            <p class="my-16">{{ $t('vdab.full_service_contact_description') }}</p>
                            <div class="grid grid-cols-2 gap-16">
                                <simple-input
                                    v-model="form.name"
                                    :errors="form.errors.name"
                                    :disabled="!creating"
                                    class="pr-8"
                                    :input-label="$t('users.fields.name') + ' *'"
                                />

                                <simple-input
                                    v-model="form.email"
                                    :errors="form.errors.email"
                                    :disabled="!creating"
                                    class="pr-8"
                                    :input-label="$t('users.fields.email') + ' *'"
                                />

                                <simple-input
                                    v-model="form.phone"
                                    :errors="form.errors.phone"
                                    :disabled="!creating"
                                    class="pr-8"
                                    :input-label="$t('settings.users.phone') + ' *'"
                                />
                            </div>
                        </template>
                    </div>
                </section>
            </div>
        </div>

        <div class="flex items-center justify-end gap-16">
            <simple-button
                v-if="!creating && form.active"
                display="danger"
                look="primary"
                small
                @click="closeVdabJob"
            >
                {{ $t('vdab.close_publication') }}
            </simple-button>
            <simple-button
                display="secondary"
                :loading="form.submitting"
                look="primary"
                small
                v-if="!isDisabled"
                @click="saveVdabJob"
            >
                {{ $t('general.publish') }}
            </simple-button>
            <simple-button
                v-else
                small
                @click="closeMe"
            >
                {{ $t('general.close') }}
            </simple-button>
        </div>

        <popup-modal-confirm
            v-if="closingVdabJob"
            :confirmText="$t('vdab.close_publication')"
            :message="$t('vdab.close_confirm')"
            :working="closingWorking"
            @close="cancelCloseVdabJob"
            @confirm="closeVdabJob(false)">
        </popup-modal-confirm>
    </div>
</template>

<script>
import Loading from './Loading.vue';
import PopupModal from './PopupModal.vue';
import SvgIcon from './SvgIcon.vue';
import Input from '../forms/Input.vue';
import Form from '../../etc/forms/Form';
import DatePicker from '../forms/DatePicker.vue';
import SimpleButton from './SimpleButton.vue';
import * as vdabJobService from '../../services/VdabJobService';
import * as vdabService from '../../services/VdabService';
import * as locationService from '../../services/LocationService';
import VdabJob from '../../models/VdabJob';
import Job from '../../models/Job';
import Location from '../../models/Location';
import {normalizeJson, ucfirst} from '../../../shared/helpers';
import moment from 'moment';
import SelectDropdown from '../forms/SelectDropdown.vue';
import Toggle from '../forms/Toggle.vue';
import {ADD_NOTIFICATION} from '../../state/mutation-types';
import JobCategorySelector from './JobCategorySelector.vue';
import StudiesSelector from './StudiesSelector.vue';
import DriverLicencesSelector from './DriverLicencesSelector.vue';
import LanguagesSelector from './LanguagesSelector.vue';
import Checkbox from '../forms/Checkbox.vue';
import PopupModalConfirm from './PopupModalConfirm.vue';
import shortid from 'shortid';
import Wysiwyg from '../forms/Wysiwyg';
import TextArea from '../forms/TextArea';
import MoreInfo from "./MoreInfo.vue";
import Banner from "./Banner.vue";
import Search from "./Search.vue";
import {mapStores} from "pinia";
import {useMainStore} from "../../state/store";
import {useNotificationsStore} from "../../state/modules/notifications";
import {useJobsStore} from "../../state/modules/jobs";
import {useRepo} from "pinia-orm";

function getFields(job, company, locale, translatable_only = false, vdabJob) {
    let fields = [
        {name: 'description', value: job.description[locale], ref: true},
        {name: 'profile', value: job.profile[locale], ref: true},
        {name: 'offer', value: job.offer[locale], ref: true},
        {name: 'title', value: job.title[locale]},
        {name: 'locale', value: locale}
    ];

    if (translatable_only) {
        return fields;
    }

    return [...fields, ...
        [
            {name: 'company_location_id', value: null},
            {name: 'category', value: {}, rules: ['required']},
            {name: 'number_to_fill', value: '', rules: ['required']},
            {name: 'active', value: true},
            {name: 'studies', value: [{uuid: shortid.generate(), id: null}]},
            {name: 'languages', value: []},
            {name: 'driver_licences', value: []},
            {name: 'serv_ids', value: []},
            {name: 'competences', value: []},
            {name: 'study_required', value: true},
            {name: 'experience', value: null},
            {name: 'experience_is_enough', value: false},
            {name: 'full_service', value: false},
            {name: 'how_to_apply_comments', value: ''},
            {name: 'name', value: '', rules: ['required']},
            {name: 'email', value: '', rules: ['required']},
            {name: 'phone', value: '', rules: ['required']},
            {
                name: 'reference',
                value: job.reference && job.reference_number.length > 0 ?
                    job.reference_number :
                    useMainStore().currentCompany.id + '-' + moment().format('YYYYMMDDhhmmss'),
                rules: ['required'],
            },
            {
                name: 'contract_start_date',
                value: null,
                accessor: value => value ? moment(value).format('YYYY-MM-DD') : null,
            },
            {
                name: 'contract_end_date',
                value: null,
                accessor: value => value ? moment(value).format('YYYY-MM-DD') : null,
            },
            {
                name: 'start_date',
                value: job.start_date,
                accessor: value => value ? moment(value).format('YYYY-MM-DD') : null,
            },
            {
                name: 'end_date',
                value: job.end_date,
                accessor: value => value ? moment(value).format('YYYY-MM-DD') : null,
            },
        ]
    ];
}

export default {
    components: {
        Search,
        Banner,
        MoreInfo,
        TextArea,
        Wysiwyg,
        'svg-icon': SvgIcon,
        'popup-modal': PopupModal,
        'simple-input': Input,
        'date-picker': DatePicker,
        'select-dropdown': SelectDropdown,
        'simple-button': SimpleButton,
        'toggle': Toggle,
        'loading': Loading,
        'job-category-selector': JobCategorySelector,
        'studies-selector': StudiesSelector,
        'driver-licences-selector': DriverLicencesSelector,
        'languages-selector': LanguagesSelector,
        'checkbox': Checkbox,
        'popup-modal-confirm': PopupModalConfirm,
    },
    props: {
        vdabJob: {
            type: VdabJob,
        },
        job: {
            type: Job,
            required: true,
        },
        scrollToTop: {
            type: Function,
            default: () => {},
        },
    },
    data: function () {
        return {
            form: this.generateForm(),
            savingTemplate: false,
            fetchingCategory: false,
            showCategoryCompetences: true,
            availableServCompetences: this.hydrateCompetences(),
            competenceFilter: '',
            closingVdabJob: false,
            closingWorking: false,
            permanentJob: ![3, 8, 13, 15, 18, 21].includes(this.job.contract_type),
        };
    },
    computed: {
        ...mapStores(useMainStore, useNotificationsStore, useJobsStore),
        currentCompany() {
            return useMainStore().currentCompany;
        },
        title() {
            return this.$t(this.creating ? 'vdab.vdabJob.add' : 'vdab.vdabJob.edit');
        },
        creating() {
            return !(this.vdabJob !== null && this.vdabJob.vdab_id !== null);
        },
        selectedServCompetences() {
            return this.availableServCompetences.filter(competence => this.form.serv_ids.includes(competence.id));
        },
        filteredServCompetences() {
            return this.availableServCompetences.filter(competence => {
                return competence.name.trim().toLowerCase().includes(this.competenceFilter.trim().toLowerCase());
            });
        },
        publishedAt() {
            return this.vdabJob.published_at ?
                moment(this.vdabJob.published_at) : null;
        },
        useLocationSelector() {
            return this.currentCompany.vdab_has_multiple_ids &&
                this.currentCompany.vdab_live;
        },
        isDisabled() {
            return false;
        },
        daysUntilUnpublished() {
            if (!this.vdabJob.active || !this.vdabJob.end_date) {
                return null;
            }

            return moment(this.vdabJob.end_date).diff(moment(), 'days') + 2;
        },
        isExpiring() {
            return this.daysUntilUnpublished !== null && parseInt(this.daysUntilUnpublished) <= 7;
        },
        getCurrentlyActiveJobsiteLanguages() {
            return this.currentCompany.jobsite.active_languages.map(language => {
                return {
                    label: "languages." + language + ".title",
                    value: language
                }
            });
        },
        locations() {
            let locations = useRepo(Location)
                .where((location) => location.vdab_id !== null)
                .get()
                .map(location => {

                    let label = '';

                    if (location.alias[this.locale] &&
                        location.alias[this.locale].length > 0) {
                        label = location.alias[this.locale];
                    } else {
                        label = location.street + ' - ' + location.postal_code + ' ' + location.city;
                    }

                    return {
                        label: label,
                        value: location.id,
                    };
                });

            if (this.currentCompany.vdab_id !== null) {
                locations.unshift({
                    label: this.currentCompany.name, value: null,
                });
            }

            return locations;
        },
    },
    methods: {
        ...mapStores(useMainStore, useNotificationsStore, useJobsStore),
        ucfirst(str) {
            return ucfirst(str);
        },
        async closeVdabJob(confirm = true) {
            if (!confirm) {
                this.closingWorking = true;
                this.formatBeforeSave();

                await vdabJobService.update(this.job.id, {
                    data: {attributes: {active: false}},
                }).then(result =>
                    useRepo(VdabJob).save(normalizeJson(result))
                ).then(result => {
                    this.closingWorking = false;
                    return useNotificationsStore()[ADD_NOTIFICATION]({
                        type: 'success',
                        message: this.$t('vdab.close_success'),
                    });
                }).then(() => this.$router.go());
            } else {
                this.closingVdabJob = true;
            }
        },
        cancelCloseVdabJob() {
            this.closingVdabJob = false;
        },
        replaceTranslatableFields(locale) {
            const translatableFields = getFields(this.job, this.currentCompany, locale, true, this.vdabJob);
            translatableFields.forEach((field, index) => {
                if (field.ref) {
                    this.$refs[field.name].setContent(field.value);
                } else {
                    this.form[field.name] = field.value;
                }
            });
        },
        generateForm() {
            if (useJobsStore().vdabEditorDrafts.hasOwnProperty(this.job.id)) {
                return useJobsStore().vdabEditorDrafts[this.job.id];
            }

            let fields = getFields(this.job, this.currentCompany, useMainStore().locale, false, this.vdabJob);

            if (this.vdabJob) {
                fields.forEach((field, index) => {
                    if(this.vdabJob.hasOwnProperty(field.name)) {
                        fields[index].value = JSON.parse(JSON.stringify(this.vdabJob[field.name] || null));
                    }

                    if(!this.vdabJob.vdab_id && field.name === 'reference') {
                        fields[index].value = this.currentCompany.id + '-' + moment().format('YYYYMMDDhhmmss');
                    }

                    if (field.name === 'active') {
                        fields[index].value = true;
                    }
                });
            }

            const form = new Form(fields);

            if (!form.experience) {
                if (this.job.wanted_experience === 0) {
                    form.experience = 0;
                }
                if (this.job.wanted_experience === 1) {
                    form.experience = 1;
                }
                if (this.job.wanted_experience === 2) {
                    form.experience = 3;
                }
                if (this.job.wanted_experience === 3) {
                    form.experience = 5;
                }
                if (this.job.wanted_experience === 4) {
                    form.experience = 10;
                }
            }

            return form;
        },
        hydrateCompetences() {
            if (!useJobsStore().vdabEditorDrafts.hasOwnProperty(this.job.id)) {
                return [];
            }

            return useJobsStore().vdabEditorDrafts[this.job.id].data.category.competences || [];
        },
        async saveVdabJob() {
            this.formatBeforeSave();

            if (this.vdabJob && this.vdabJob.id) {
                await this.updateVdabJob();
            } else {
                await this.createVdabJob();
            }
        },
        createVdabJob() {
            return this.form.submit(data => {
                return vdabJobService.store(this.job.id, {
                    data: {
                        attributes: data,
                        relationships: {
                            companyLocation: {
                                data: data.company_location_id ? {
                                    type: 'locations',
                                    id: data.company_location_id,
                                } : null,
                            },
                        },
                    },
                });
            }).then(result =>
                useRepo(VdabJob).save(normalizeJson(result)),
                this.scrollToTop()
            ).then(() => {
                return useNotificationsStore()[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('vdab.post_success'),
                });
            })
        },
        updateVdabJob() {
            return this.form.submit(data => {
                return vdabJobService.update(this.job.id, {
                    data: {
                        attributes: data,
                        relationships: {
                            companyLocation: {
                                data: data.company_location_id ? {
                                    type: 'locations',
                                    id: data.company_location_id,
                                } : null,
                            },
                        },
                    },
                });
            }).then((result) => {
                useRepo(VdabJob).save(normalizeJson(result))
                this.scrollToTop()
            }).then(() => {
                return useNotificationsStore()[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('vdab.post_success'),
                });
            }).catch(error => {
                //check if VDAB job is still active on VDAB
                if (error.response.data.errors['data.attributes.status'] &&
                    error.response.data.errors['data.attributes.status'].includes(this.$t('validation.vdab.no_longer_active'))) {
                    useRepo(VdabJob).save({
                        id: this.vdabJob.id,
                        vdab_url: null,
                        vdab_id: null,
                        active: false,
                        status: 'inactive',
                        start_date: null,
                        end_date: null,
                        published_at: null,
                        published: false,
                    });
                }
            });
        },
        formatBeforeSave() {
            if (this.form.data.start_date === '') {
                this.form.data.start_date = null;
            }
            if (this.form.data.start_date) {
                this.form.data.start_date = moment(this.form.data.start_date).format('YYYY-MM-DD');
            }

            if (this.form.data.end_date === '') {
                this.form.data.end_date = null;
            }
            if (this.form.data.end_date) {
                this.form.data.end_date = moment(this.form.data.end_date).format('YYYY-MM-DD');
            }

            //remove empty values && check no requirements
            let studies = this.form.data.study_required ?
                [...this.form.data.studies] : [VdabJob.noRequiredStudy()];

            studies = studies.filter((study) => study.id !== null);

            //merge all competences
            this.form.data.competences = VdabJob.mergeCompetences(
                this.selectedServCompetences,
                studies,
                this.form.data.languages,
                this.form.data.driver_licences);
        },
        changeCategory(category) {
            this.form.data.serv_ids = [];
            this.setCategory(category);
        },
        setCategory(category) {
            this.fetchingCategory = true;
            vdabService.showCategory(category.id)
                .then(result => {
                    this.form.data.category = result;
                    this.availableServCompetences = [...result.competences];
                    this.form.data.full_service = result.knelpuntberoep;
                }).finally(() => {
                this.fetchingCategory = false;
            });
        },
        toggleCategoryCompetences() {
            this.showCategoryCompetences = !this.showCategoryCompetences;
        },
        // setStudies(studies) {
        //     //this.form.data.studies = studies;
        // },
        setDriverLicences(licences) {
            this.form.data.driver_licences = licences;
        },
        setLanguages(languages) {
            this.form.data.languages = languages;
        },
        resetCompetenceFilter() {
            this.competenceFilter = '';
        },
        closeMe() {
            useJobsStore().saveVdabDraft({job: this.job.id, form: this.form});
            this.$emit('close');
        },
    },
    mounted() {
        if (this.vdabJob && this.vdabJob.category.id) {
            this.setCategory(this.vdabJob.category);
        }

        //get company location for vdab
        locationService.index().then(result => {
            return useRepo(Location).save(normalizeJson(result.data));
        });
    },
    watch: {},
};
</script>

<style scoped>
.slide-fade-enter-active {
    transition: all .3s ease;
}

.slide-fade-leave-active {
    transition: all .3s ease;
}

.slide-fade-enter, .slide-fade-leave-to {
    opacity: 0;
}

.slide-fade-enter {
    transform: translateY(-10px);
}

.slide-fade-leave-to {
    transform: translateY(10px);
}
</style>
